<!-- @format -->

<template>
  <FlexCol gap-3 class="border-gray-200 divide-y">
    <!-- Cabeçalho -->
    <Flex justify-between items-center gap-3>
      <h2 class="text-lg font-bold">#{{ formatProtocol(localValues.protocolo) }}</h2>
      <span
        v-if="localValues.status"
        class="px-3 py-1 text-xs font-medium rounded-[2px]"
        :class="localValues.status === 'EXECUTADO' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'">
        {{ localValues.status }}
      </span>
    </Flex>

    <!-- Serviço -->
    <div class="py-3">
      <p class="text-xs text-gray-500">Serviço</p>
      <p>{{ localValues.idServiceRequested }} - {{ localValues.serviceName }}</p>
    </div>

    <!-- Identificadores -->
    <div class="py-3 grid grid-cols-4 gap-3">
      <div>
        <p class="text-xs text-gray-500">Equipe</p>
        <p>{{ localValues.idTeam }}</p>
      </div>
      <div>
        <p class="text-xs text-gray-500">Serviço Exec</p>
        <p>{{ localValues.idServicePerformed }}</p>
      </div>
      <div>
        <p class="text-xs text-gray-500">Localidade</p>
        <p>{{ localValues.idLocale }}</p>
      </div>
      <div>
        <p class="text-xs text-gray-500">Distrito</p>
        <p>{{ localValues.idDistrict }}</p>
      </div>

      <div v-if="localValues.idServiceGroup">
        <p class="text-xs text-gray-500">Grupo de Serviço</p>
        <p>{{ localValues.idServiceGroup }}</p>
      </div>
      <div v-if="localValues.idTeamType">
        <p class="text-xs text-gray-500">Tipo de Equipe</p>
        <p>{{ localValues.idTeamType }}</p>
      </div>
    </div>

    <!-- Datas -->
    <div class="py-3 grid grid-cols-3 gap-3">
      <div>
        <p class="text-xs text-gray-500">Data Solicitação</p>
        <p>{{ formatFullDate(localValues.requestedDate) }}</p>
      </div>
      <div>
        <p class="text-xs text-gray-500">Data Execução</p>
        <p>{{ formatFullDate(localValues.performedDate) }}</p>
      </div>
      <div>
        <p class="text-xs text-gray-500">Data Expiração</p>
        <p>{{ formatFullDate(localValues.expirationDate) }}</p>
      </div>
    </div>

    <!-- Endereço -->
    <div class="py-3">
      <p class="text-xs text-gray-500">Endereço</p>
      <p>
        {{ localValues.address }}, {{ localValues.addressNumber }} <br />
        {{ localValues.neighborhood }} <span v-if="localValues.cep">(CEP: {{ localValues.cep }})</span>
      </p>
    </div>

    <!-- Observação -->
    <div class="py-3">
      <p class="text-xs text-gray-500">Observação</p>
      <p>{{ localValues.observation }}</p>
    </div>

    <div v-if="normalizedPhotos.length > 0" class="py-3">
      <p class="text-xs text-gray-500 mb-3">Fotos da OS</p>
      <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
        <a
          v-for="photo in normalizedPhotos"
          :key="photo.id || photo.fileName"
          :href="photo.url"
          target="_blank"
          rel="noopener noreferrer"
          class="block overflow-hidden rounded border border-gray-200 bg-gray-50 hover:border-primary transition-colors">
          <img :src="photo.url" :alt="photo.originalName || photo.fileName || 'Foto da OS'" class="h-40 w-full object-cover" />
        </a>
      </div>
    </div>

    <FlexCol gap-3>
      <T4 v-if="localValues.services && localValues.services.length > 0">Serviços Baixados</T4>
      <SimpleTable
        v-if="localValues.services && localValues.services.length > 0"
        :headers="headersService"
        :items="localValues.services"
        :totalItems="localValues.services.length"
        disablePagination>
        <template #serviceName="{ item }">
          <span v-if="item.serviceName">{{ item.serviceName }}</span>
          <span v-else class="italic"> Serviço Não Registrado </span>
        </template>

        <template v-slot:idService="{ item }">
          <span class="roboto-mono-400">
            {{ `${item.idService}`.padStart(4, '0') }}
          </span>
        </template>
        <template v-slot:price="{ item }">
          {{ formatMoneyS(item.price) }}
        </template>
      </SimpleTable>
      <SimpleTable v-if="localValues.mos && localValues.mos.length > 0" :headers="headersMos" :items="localValues.mos" :totalItems="localValues.mos.length" disablePagination>
        <template #mosName="{ item }">
          <span v-if="item.mosName">{{ item.mosName }}</span>
          <span v-else class="italic"> Serviço Não Registrado </span>
        </template>

        <template v-slot:idMos="{ item }">
          <span class="roboto-mono-400" v-if="item.mosIdService"> {{ `${item.mosIdService}`.padStart(4, '0') }} / </span>
          <span class="roboto-mono-400"> {{ formatMos(item.idMos) }} </span>
        </template>
        <template #quantity1="{ item }">
          {{ formatQuantity(item.quantity1) }}
        </template>
        <template #quantity2="{ item }">
          {{ formatQuantity(item.quantity2) }}
        </template>
        <template #quantity3="{ item }">
          {{ formatQuantity(item.quantity3) }}
        </template>

        <template #total="{ item }">
          {{ sumTotal(item).toFixed(2) }}
        </template>
        <template v-slot:price="{ item }">
          {{ formatMoneyS(item.price) }}
        </template>
        <template v-slot:value="{ item }">
          {{ formatMoneyS(item.value) }}
        </template>
      </SimpleTable>

      <T4 v-if="localValues.measures && localValues.measures.length > 0">Serviços/MOS Pagos</T4>
      <SimpleTable
        v-if="localValues.measures && localValues.measures.length > 0"
        :headers="headersMeasure"
        :items="localValues.measures"
        :totalItems="localValues.measures.length"
        disablePagination>
        <template v-slot:code="{ item }">
          <span class="roboto-mono-400"> {{ `${item.idServicePerformerd}`.padStart(4, '0') }} / {{ formatMos(item.idMos) }} </span>
        </template>
        <template v-slot:price="{ item }">
          {{ formatMoneyS(item.price) }}
        </template>
        <template v-slot:sgmGlosado="{ item }"> <IconSolid icon="fa-circle-exclamation" v-if="item.sgmGlosado == 'S'" class="text-red-500" /> </template>
        <template v-slot:mosGlosado="{ item }"> <IconSolid icon="fa-circle-exclamation" v-if="item.mosGlosado == 'S'" class="text-red-500" /> </template>
      </SimpleTable>

      <T4 v-if="localValues.materials && localValues.materials.length > 0">Materiais Baixados</T4>
      <SimpleTable
        v-if="localValues.materials && localValues.materials.length > 0"
        :headers="headersMaterials"
        :items="localValues.materials"
        :totalItems="localValues.materials.length"
        disablePagination>
        <template v-slot:quantity="{ item }">
          <span>{{ formatQuantity(item.quantity) }}</span>
        </template>

        <template #name="{ item }">
          <span v-if="item.name">{{ item.name }}</span>
          <span v-else class="italic"> Material Não Registrado </span>
        </template>
      </SimpleTable>
    </FlexCol>
  </FlexCol>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useFormat } from '@/composables/useFormat'
import { baseApiUrl } from '@/global'
import { watch } from 'vue'

const { formatProtocol, formatFullDate, formatMoneyS, formatMos } = useFormat()

const props = defineProps({
  modelValue: Object,
  order: Object,
})

const localValues = ref(props.order)

const normalizedPhotos = computed(() => {
  const photos = Array.isArray(localValues.value?.photos) ? localValues.value.photos : []

  return photos
    .map((photo) => {
      const url = photo?.fileUrl || (photo?.fileName ? `${baseApiUrl}/uploads/${photo.fileName}` : null)
      if (!url) return null

      return {
        ...photo,
        url,
      }
    })
    .filter(Boolean)
})

const headersService = [
  { title: 'Código', field: 'idService', width: '200px' },
  { title: 'Serviço', field: 'serviceName', error: 'Serviço não cadastrado ' },
  { title: 'Valor', field: 'price', width: '120px' },
  // { title: 'Pontos', field: 'points', width: '120px' },
]

const headersMaterials = [
  { title: 'Código', field: 'idMaterial', width: '120px' },
  { title: 'Nome', field: 'name', error: 'Material não cadastrado' },
  { title: 'Quantidade', field: 'quantity', width: '120px' },
]

const headersMeasure = [
  { title: 'Código', field: 'code', width: '200px' },
  { title: 'Serviço', field: 'serviceName' },
  { title: 'SGM Glos.', field: 'sgmGlosado', width: '120px' },
  { title: 'MOS Glos.', field: 'mosGlosado', width: '120px' },
  { title: 'Quant.', field: 'quantityTotal', width: '120px' },
  { title: 'Valor', field: 'price', width: '120px' },
  // { title: 'Pontos', field: 'points', width: '120px' },
]

const headersMos = [
  { title: 'Código', field: 'idMos', width: '200px' },
  { title: 'MOS', field: 'mosName' },
  { title: 'QNTD', field: 'totalMos', width: '90px' },
  // { title: 'Total', field: 'total', width: '90px' },
  // { title: 'Pontos', field: 'points', width: '90px' },
]

const formatQuantity = (value) => {
  if (!value) {
    return null
  }
  return (value / 100).toFixed(2)
}

const getTotalQuantity = (item) => {
  if (item.quantity3 >= 1) return 3
  if (item.quantity2 >= 1) return 2
  return 1
}

const mapTotalMos = (items) => {
  for (const mos of items) {
    mos.totalMos = getTotalQuantity(mos)
  }
}

const sumTotal = (item) => {
  let total = 0
  if (!item) {
    return total
  }
  if (item.quantity1) {
    total = item.quantity1 / 100
  }
  if (item.quantity2) {
    total *= item.quantity2 / 100
  }
  if (item.quantity3) {
    total *= item.quantity3 / 100
  }
  return total
}
watch(
  () => props.order,
  (newVal) => {
    if (newVal && newVal.mos.length >= 1) {
      return mapTotalMos(newVal.mos)
    }
  },
  { deep: true, immediate: true }
)

watch(
  () => props.order,
  () => {
    localValues.value = props.order
  },
  { deep: true }
)
</script>
